import React, { useEffect, useRef } from 'react';
import { Box, Typography, LinearProgress, Paper, List, ListItem, ListItemIcon, ListItemText, Collapse, IconButton } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useLanguage } from '../contexts/LanguageContext';

const FlowchartView = ({
  nodes,
  isLoading,
  isLocalizationLoading = false,
  translateContent = (value) => value,
  scenarioData,
  checkpointStatus
}) => {
  const { tr, isEnglish } = useLanguage();
  const [expandedNodes, setExpandedNodes] = React.useState({});
  const activeNodeRef = useRef(null);
  const contentLoading = isLoading || (isEnglish && isLocalizationLoading);

  const handleNodeClick = (nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  // Expand active node and scroll to it when loading completes
  useEffect(() => {
    if (!isLoading && nodes) {
      // Find active node and expand it
      let activeNodeId = null;
      
      // Traverse through all stages and nodes to find the active one
      nodes.forEach(stage => {
        stage.nodes.forEach(node => {
          if (node.status === 'active') {
            activeNodeId = node.id;
          }
        });
      });
      
      // Expand the active node if found
      if (activeNodeId) {
        setExpandedNodes(prev => ({
          ...prev,
          [activeNodeId]: true
        }));
      }
      
      // Scroll to active node
      if (activeNodeRef.current) {
        setTimeout(() => {
          activeNodeRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }, 100); // Small delay to ensure DOM is updated
      }
    }
  }, [isLoading, nodes]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success.main';
      case 'active':
        return 'primary.main';
      case 'pending':
        return 'grey.500';
      default:
        return 'grey.500';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return tr('已完成', 'Completed');
      case 'active':
        return tr('進行中', 'In Progress');
      case 'pending':
        return tr('待處理', 'Pending');
      default:
        return tr('待處理', 'Pending');
    }
  };

  const renderCheckpoints = (node) => {
    if (!node.checkpoint_status) return null;

    return (
      <List dense>
        {node.checkpoint_status.map((checkpoint, index) => (
          <ListItem key={index}>
            <ListItemIcon>
              {checkpoint.completed ? (
                <CheckCircleIcon color="success" />
              ) : (
                <RadioButtonUncheckedIcon color="disabled" />
              )}
            </ListItemIcon>
            <ListItemText 
              primary={translateContent(checkpoint.text)}
              sx={{
                textDecoration: checkpoint.completed ? 'line-through' : 'none',
                color: checkpoint.completed ? 'text.secondary' : 'text.primary'
              }}
            />
          </ListItem>
        ))}
      </List>
    );
  };

  const renderNode = (node) => {
    const isExpanded = expandedNodes[node.id] || false;
    const hasCheckpoints = node.checkpoint_status && node.checkpoint_status.length > 0;
    const isActive = node.status === 'active';

    return (
      <Paper 
        key={node.id}
        ref={isActive ? activeNodeRef : null}
        sx={{ 
          mb: 2,
          p: 2,
          borderLeft: 4,
          borderColor: getStatusColor(node.status),
          bgcolor: isActive ? 'primary.lighter' : 'background.paper'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {translateContent(node.text)}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: getStatusColor(node.status),
                bgcolor: `${getStatusColor(node.status)}20`,
                px: 1,
                py: 0.5,
                borderRadius: 1
              }}
            >
              {getStatusText(node.status)}
            </Typography>
          </Box>
          {hasCheckpoints && (
            <IconButton size="small" onClick={() => handleNodeClick(node.id)}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>

        {node.completion_percentage !== undefined && (
          <Box sx={{ mt: 1 }}>
            <LinearProgress 
              variant="determinate" 
              value={node.completion_percentage} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  bgcolor: getStatusColor(node.status)
                }
              }}
            />
            <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
              {Math.ceil(node.completion_percentage)}% {tr('完成', 'Complete')}
            </Typography>
          </Box>
        )}

        {hasCheckpoints && (
          <Collapse in={isExpanded}>
            {renderCheckpoints(node)}
          </Collapse>
        )}

        {node.goal && (
          <Typography 
            variant="body2" 
            sx={{ 
              mt: 1, 
              color: 'text.secondary',
              fontStyle: 'italic'
            }}
          >
            {tr('目標', 'Goal')}: {translateContent(node.goal)}
          </Typography>
        )}
      </Paper>
    );
  };

  const renderStage = (stage) => {
    return (
      <Box key={stage.id} sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 2,
            color: 'primary.main',
            fontWeight: 'bold'
          }}
        >
          {translateContent(stage.text)}
        </Typography>
        {stage.nodes.map(node => renderNode(node))}
      </Box>
    );
  };

  if (contentLoading) {
    return (
      <Box sx={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <LinearProgress sx={{ mb: 2, width: '200px' }} />
            <Typography>{tr('載入中...', 'Loading English scenario…')}</Typography>
          </Box>
        </Box>
        {!isLocalizationLoading && (
          <Box sx={{ opacity: 0.5, p: 2 }}>
            {nodes && nodes.map(stage => renderStage(stage))}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {nodes && nodes.map(stage => renderStage(stage))}
    </Box>
  );
};

export default FlowchartView;
